/**
 * @typedef {import('./Props').default} Props
 * @typedef {import('../../../../shared/AuthProvider/State').default} AuthState
 */
import { Link } from 'react-router-dom';
import { translate } from '../../../Internationalization';
import { AuthContext } from '../../../../shared/AuthProvider';
import ExternalAnchor from '../../../../shared/ExternalAnchor';
import FieldRadio from '../../../../shared/Forms/Fields/FieldRadio';
import paths from '../../../../config/local/paths';
import React from 'react';

const SUBSCRIBED_TEXT = translate('import_location.subscribed');
const NOT_SUBSCRIBED_TEXT = translate('import_location.not_subscribed');

/** @extends {React.Component<Props>} */
export class GroupSelection extends React.Component {
	/** @type {AuthState} */
	context = /** @type {AuthState} */ ({});

	/**
	 * @returns {[string, string, boolean][]}
	 */
	#getOptions() {
		/** @type {[string, string, boolean][]} */
		const options = [
			[
				'',
				translate('admin.groups.group_selection.no_group'),
				this.#resolveSubscription(),
			],
		];

		this.props.groups.forEach((group) => {
			options.push([
				group.id,
				group.name,
				this.#resolveSubscription(group.hasSubscription),
			]);
		});

		return options;
	}

	/**
	 * @param {boolean} [hasSubscription]
	 * @returns {boolean}
	 */
	#resolveSubscription(hasSubscription = false) {
		if (this.context?.resolveSubscription) {
			return this.context.resolveSubscription(hasSubscription);
		}

		return !!hasSubscription;
	}

	/**
	 * @param {boolean} [hasSubscription]
	 * @returns {JSX.Element}
	 */
	#getSubscriptionLink(hasSubscription = false) {
		if (this.#resolveSubscription(hasSubscription)) {
			return (
				<ExternalAnchor
					className="hover-color-primary no-underline text-theme font-bold"
					href={paths.help.moverIntel}
				>
					{translate('import_location.mover_intel')}
				</ExternalAnchor>
			);
		}

		return (
			<Link
				className="no-underline text-theme font-bold"
				to={paths.app.agent.subscription.root}
			>
				{translate('import_location.mover_intel')}
			</Link>
		);
	}

	/** @returns {JSX.Element} */
	get #noGroupOption() {
		return (
			<li className="my-6">
				<FieldRadio
					label={`${translate(
						'admin.groups.group_selection.no_group'
					)} - ${
						this.#resolveSubscription()
							? SUBSCRIBED_TEXT
							: NOT_SUBSCRIBED_TEXT
					}`}
					labelSpanClass="font-bold"
					name="groupId"
					onClick={this.props.onClick}
					value=""
				/>
				<ul className="ml-12 text-sm">
					<li className="list-disc">
						<span className="font-bold">
							{translate('import_location.private')}
						</span>
						{translate('import_location.private_description')}
					</li>
					<li className="list-disc">
						<span className="font-bold">
							{translate('import_location.subscription')}
						</span>
						{translate('import_location.personal_subscription')}
						{this.#getSubscriptionLink()}
					</li>
				</ul>
			</li>
		);
	}

	/** @returns {JSX.Element[]} */
	get #groupOptions() {
		return this.#getOptions()
			.filter(([id]) => id)
			.sort(GroupSelection.#sortByName)
			.map(([id, name, hasSubscription]) => (
				<li className="my-8" key={id}>
					<FieldRadio
						label={`${name} - ${
							hasSubscription
								? SUBSCRIBED_TEXT
								: NOT_SUBSCRIBED_TEXT
						}`}
						labelSpanClass="font-bold"
						name="groupId"
						onClick={this.props.onClick}
						value={id}
					/>
					<ul className="ml-12 text-sm">
						<li className="list-disc">
							<span className="font-bold">
								{translate('import_location.collaborative')}
							</span>
							{translate(
								'import_location.collaborative_description'
							)}
						</li>
						<li className="list-disc">
							<span className="font-bold">
								{translate('import_location.subscription')}
							</span>
							{translate('import_location.group_subscription')}
							{this.#getSubscriptionLink(hasSubscription)}
						</li>
					</ul>
				</li>
			));
	}

	/**
	 * @param {[string, string, boolean]} a
	 * @param {[string, string, boolean]} b
	 * @returns {number}
	 */
	static #sortByName(a, b) {
		return a[1] < b[1] ? -1 : 1;
	}

	/** @returns {JSX.Element} */
	render() {
		return (
			<fieldset className="mb-8">
				<legend className="h-ms-3 mb-6">
					{translate('import_location.title')}
				</legend>

				<section className="input-group radio-group">
					<ul className="my-6">
						{this.#noGroupOption}
						{this.#groupOptions}
					</ul>
				</section>
			</fieldset>
		);
	}
}

GroupSelection.contextType = AuthContext;

export default GroupSelection;
